/**
 * Auth state. Kept in zustand (not react-query) because the token and the
 * signed-in user drive routing, and routing must resolve synchronously.
 *
 * The user carries their **permissions** and their **landing route**, both
 * decided by the backend. Nothing in the UI should ask "is this an ADMIN?" —
 * ask `can('USER_MANAGE')` instead, so a workspace-defined role behaves
 * correctly without a frontend change.
 */

import { create } from 'zustand';
import { authApi } from '@/api';
import { setUnauthorizedHandler, tokenStore } from '@/lib/http';
import { storageKeys } from '@/lib/env';
import { queryClient } from '@/lib/queryClient';
import type { AuthUser, LoginRequest, Permission, Role } from '@/types';

interface AuthState {
  user: AuthUser | null;
  status: 'idle' | 'authenticating';
  login: (credentials: LoginRequest) => Promise<AuthUser>;
  logout: (options?: { silent?: boolean }) => Promise<void>;
  setUser: (user: AuthUser) => void;
  /**
   * Re-reads the signed-in user from the API.
   *
   * Permissions live on the *role*, so an administrator editing roles can change
   * their own capabilities — or someone else's — while the app is running. The
   * RBAC screens call this afterwards so the sidebar and the guards reflect the
   * new grants without a sign-out.
   */
  refresh: () => Promise<void>;
  /** True when the signed-in user holds **every** permission listed. */
  can: (...permissions: Permission[]) => boolean;
  /** True when the signed-in user holds **any** of the permissions listed. */
  canAny: (...permissions: Permission[]) => boolean;
  /** Escape hatch for the rare place a role code genuinely matters. */
  hasRole: (...roles: Role[]) => boolean;
}

function readStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(storageKeys.user);
    if (!raw || !tokenStore.get()) return null;
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: readStoredUser(),
  status: 'idle',

  login: async (credentials) => {
    set({ status: 'authenticating' });
    try {
      const response = await authApi.login(credentials);
      tokenStore.set(response.accessToken);
      localStorage.setItem(storageKeys.refreshToken, response.refreshToken);
      localStorage.setItem(storageKeys.user, JSON.stringify(response.user));
      set({ user: response.user, status: 'idle' });
      return response.user;
    } catch (error) {
      set({ status: 'idle' });
      throw error;
    }
  },

  logout: async (options) => {
    if (!options?.silent) {
      // Best effort — the local session is cleared either way.
      await authApi.logout().catch(() => undefined);
    }
    tokenStore.clear();
    localStorage.removeItem(storageKeys.user);
    set({ user: null });
    queryClient.clear();
  },

  setUser: (user) => {
    localStorage.setItem(storageKeys.user, JSON.stringify(user));
    set({ user });
  },

  refresh: async () => {
    if (!get().user) return;
    try {
      const user = await authApi.me();
      localStorage.setItem(storageKeys.user, JSON.stringify(user));
      set({ user });
    } catch {
      // A failed refresh must never sign anyone out: a 401 is already handled by
      // the interceptor, and anything else is transient.
    }
  },

  can: (...permissions) => {
    const granted = get().user?.permissions;
    if (!granted) return false;
    return permissions.every((permission) => granted.includes(permission));
  },

  canAny: (...permissions) => {
    const granted = get().user?.permissions;
    if (!granted) return false;
    return permissions.some((permission) => granted.includes(permission));
  },

  hasRole: (...roles) => {
    const role = get().user?.role;
    return role ? roles.includes(role) : false;
  },
}));

/** A 401 from any request drops the local session. */
setUnauthorizedHandler(() => {
  void useAuthStore.getState().logout({ silent: true });
});

/**
 * Where a user lands after signing in.
 *
 * The backend decides this per role (`role.landingRoute`), so a workspace can
 * send its receivers straight to the scanner without a frontend release. The
 * fallback only matters for a token issued before the field existed.
 */
export function landingRouteFor(user: AuthUser | null | undefined): string {
  if (!user) return '/login';
  if (user.landingRoute) return user.landingRoute;

  switch (user.mobilePersona) {
    case 'RECEIVE':
      return '/m/receive';
    case 'DISPATCH':
      return '/m/dispatch';
    default:
      return '/dashboard';
  }
}
